import { addOutline, createOutline, listOutline } from 'ionicons/icons';
import type { ValueFormatterParams } from 'ag-grid-community';
import { ActionGridActionColumn, ActionGridBulkAction, ActionGridColumnDefinition } from '@/types/model/actionGrid';
import { CAGridRow } from '@/types/model/corrective-action';

const formatDate = (params: ValueFormatterParams): string => (
  params.value ? new Date(params.value as string).toLocaleDateString() : '—'
);

export const buildCorrectiveActionActionGridColumns = (): ActionGridColumnDefinition[] => [
  { key: 'id', headerName: 'CA #', isRowId: true, colDef: { width: 90, filter: 'agNumberColumnFilter' } },
  { key: 'issue', headerName: 'Issue', colDef: { flex: 2, minWidth: 200, filter: true, tooltipField: 'issue' } },
  { key: 'lineItem', headerName: 'Line Item', colDef: { width: 110, filter: true } },
  { key: 'lineItemTitle', headerName: 'Line Item Title', colDef: { flex: 1, minWidth: 160, filter: true, tooltipField: 'lineItemTitle' } },
  { key: 'groupTitle', headerName: 'Group', colDef: { width: 160, filter: true } },
  { key: 'statusName', headerName: 'CA Status', colDef: { width: 130, filter: true } },
  { key: 'validationStatusName', headerName: 'Validation Status', colDef: { width: 150, filter: true } },
  { key: 'classificationName', headerName: 'Classification', colDef: { width: 140, filter: true } },
  { key: 'siteName', headerName: 'Site', colDef: { width: 140, filter: true } },
  { key: 'issued', headerName: 'Issued On', colDef: { width: 120, filter: 'agDateColumnFilter', valueFormatter: formatDate } },
  { key: 'issuedByName', headerName: 'Issued By', colDef: { width: 150, filter: true } },
  { key: 'accepted', headerName: 'Accepted On', colDef: { width: 120, filter: 'agDateColumnFilter', valueFormatter: formatDate } },
  { key: 'acceptedBy', headerName: 'Accepted By', colDef: { width: 140, filter: true } },
  { key: 'lastResponseDate', headerName: 'Last Response', colDef: { width: 130, filter: 'agDateColumnFilter', valueFormatter: formatDate } },
  { key: 'lastResponseBy', headerName: 'Response By', colDef: { width: 140, filter: true } },
  { key: 'lastResponseType', headerName: 'Response Type', colDef: { width: 130, filter: true } },
  { key: 'lastResponseComments', headerName: 'Response Comments', colDef: { flex: 1, minWidth: 180, filter: true, tooltipField: 'lastResponseComments' } },
  { key: 'attachmentCount', headerName: 'Attachments', colDef: { width: 110, filter: 'agNumberColumnFilter' } },
];

export const buildCorrectiveActionActionGridData = (rows: CAGridRow[]): unknown[][] => {
  const columns = buildCorrectiveActionActionGridColumns();
  return rows.map((row) => columns.map((column) => (row as unknown as Record<string, unknown>)[column.key]));
};

interface BuildCorrectiveActionActionGridBulkActionsParams {
  canAddNew: boolean;
  onAddNew: () => void;
  onSwitchToAccordion: () => void;
}

export const buildCorrectiveActionActionGridBulkActions = ({
  canAddNew,
  onAddNew,
  onSwitchToAccordion,
}: BuildCorrectiveActionActionGridBulkActionsParams): ActionGridBulkAction[] => {
  const actions: ActionGridBulkAction[] = [];

  if (canAddNew) {
    actions.push({
      id: 'new-corrective-action',
      label: 'New Corrective Action',
      icon: addOutline,
      onAction: () => onAddNew(),
    });
  }

  actions.push({
    id: 'switch-to-accordion-view',
    label: 'Switch to Accordion View',
    icon: listOutline,
    onAction: () => onSwitchToAccordion(),
  });

  return actions;
};

interface BuildCorrectiveActionActionGridActionColumnsParams {
  rows: CAGridRow[];
  onEdit: (row: CAGridRow) => void;
}

export const buildCorrectiveActionActionGridActionColumns = ({
  rows,
  onEdit,
}: BuildCorrectiveActionActionGridActionColumnsParams): ActionGridActionColumn[] => {
  const rowsById = new Map(rows.map((row) => [String(row.id), row]));

  return [
    {
      id: 'corrective-action-row',
      headerName: 'Actions',
      width: 70,
      pinned: 'right',
      actions: [
        {
          id: 'edit-corrective-action',
          title: 'Edit Corrective Action',
          icon: createOutline,
          onAction: ({ row }) => {
            const found = rowsById.get(row.rowId);
            if (found) {
              onEdit(found);
            }
          },
        },
      ],
    },
  ];
};
